
import { BehaviorSubject, Observable, throwError as observableThrowError } from "rxjs";
import { finalize, tap, catchError, switchMap, filter, take } from 'rxjs/operators';
import { ButtonService } from "./../button.service";
import { Injectable } from "@angular/core";
import { LocalStorageService } from "angular-2-local-storage";
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpHeaderResponse,
  HttpProgressEvent,
  HttpUserEvent,
  HttpErrorResponse
} from "@angular/common/http";
import { LoaderService } from "../../../components/loader/loader.service";
import { Router } from '@angular/router';
import { AuthenticationService } from '../../api/auth/auth.service';
import { environment } from "../../../../environments/environment";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";

@Injectable()
export class InterceptorService implements HttpInterceptor {
  public pendingRequests = 0;
  public showLoading = false;

  constructor(
    private localStorageService: LocalStorageService,
    public loaderService: LoaderService,
    public buttonService: ButtonService,
    private router: Router,
    private authenticationService: AuthenticationService,
    private modalService: NgbModal
  ) { }

  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    if (req.url.toLowerCase().includes("8pxzjn7zy9.execute-api.ap-southeast-1.amazonaws.com")) {
      return req.clone();
    } 
    
    // Check if the request is going to the Intakes API
    if (req.url.includes(environment.apiIntakesURL)) {
      const intakesToken = this.localStorageService.get<string>("intakesToken");
      if (intakesToken) {
        return req.clone({
          setHeaders: {
            Authorization: `Bearer ${intakesToken}`
          }
        });
      }
    }

    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler):
    Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any> | any> {

    this.pendingRequests++;

    return next.handle(this.addToken(req, this.localStorageService.get<string>("accessToken")))
      .pipe(
        tap(
          event => {
            if (event instanceof HttpResponse) {
              this.showLoader();
              this.buttonService.onShow();
            }
          }
        ),
        catchError(error => {
          this.buttonService.onHide();
          if (error instanceof HttpErrorResponse) {
            switch ((error as HttpErrorResponse).status) {
              case 401:
                return this.handle401Error(req, next);
              case 403:
                return this.handle403Error(req, next);
              case 400:
                return this.handle400Error(error);
              default:
                // return next.handle(this.addToken(req, this.localStorageService.get<string>("accessToken")));
                return observableThrowError(error);
            }
          } else {
            return observableThrowError(error);
          }
        }),
        finalize(() => {
          this.hideLoader();
          this.buttonService.onHide();
        })
      );
  }

  handle401Error(req: HttpRequest<any>, next: HttpHandler): any {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(undefined);

      const userEmail = this.localStorageService.get<string>("userEmail");
      const accessToken = this.localStorageService.get<string>("accessToken");
      const refreshToken = this.localStorageService.get<string>("refreshToken");

      return this.authenticationService.refreshToken(userEmail, accessToken, refreshToken)
        .pipe(
          switchMap(res => {
            if (res) {
              this.tokenSubject.next(res.authenticationResult.accessToken);
              this.setToken(res.authenticationResult.accessToken,
                refreshToken,
                res.authenticationResult.expiresIn);

              return next.handle(this.addToken(req, res.authenticationResult.accessToken));
            }
            // If we don't get a new token, we are in trouble so logout.
            this.closeAllModals();
            this.router.navigateByUrl('login');
          }),
          catchError(() => {
            // If there is an exception calling 'refreshToken', bad news so logout.
            this.closeAllModals();
            return this.router.navigate([`/login`])
          }),
          finalize(() => {
            this.isRefreshingToken = false;
          }));
    } else {
      return this.tokenSubject.pipe(
        filter(token => token !== undefined),
        take(1),
        switchMap(token => {
          return next.handle(this.addToken(req, token));
        }));
    }
  }

  handle403Error(req: HttpRequest<any>, next: HttpHandler): any {
    this.closeAllModals();
    return this.router.navigateByUrl('login');
  }

  handle400Error(err: any): any {
    if (err.error === "Refresh Token has been revoked") {
      this.closeAllModals();
      this.localStorageService.clearAll();
      window.location.href = "/login";
    } else {
      return observableThrowError(err);
    }
  }

  // Session-expiry redirects bypass the router's normal teardown, so any
  // open NgbModal (e.g. Invite Employee, using backdrop: 'static') is left
  // rendered on top of the login page. Force-dismiss everything first.
  private closeAllModals(): void {
    this.modalService.dismissAll('session-expired');
  }

  showLoader(): void {
    if (!this.showLoading) {
      this.showLoading = true;
      this.loaderService.show();
    }
    this.showLoading = true;
  }

  hideLoader(): void {
    this.pendingRequests--;
    if (this.pendingRequests <= 0) {
      if (this.showLoading) {
        this.loaderService.show();
      }
      this.loaderService.hide();

      this.showLoading = false;
    }
  }

  setToken(accressToken: string, refreshToken: string, expiresTime: number): void {
    this.localStorageService.set("accessToken", accressToken);
    this.localStorageService.set("refreshToken", refreshToken);
    this.localStorageService.set("expiresTime", (Date.now() / 1000) + expiresTime);
  }
}
